/**
 * Typed wrappers for server callables. All money movement (wallet debit/credit, refunds,
 * order creation) goes through these; Firestore rules block direct client writes.
 */
import { httpsCallable } from "firebase/functions";
import { functions } from "../lib/firebase";

export const call = async <T = any>(name: string, data: any = {}): Promise<T> => {
  const fn = httpsCallable(functions, name);
  const res: any = await fn(data);
  return res.data as T;
};

/** Random idempotency key (crypto-strength when available). */
export const newIdempotencyKey = (prefix = "ik"): string => {
  const c: any = (typeof window !== "undefined" && (window as any).crypto) || undefined;
  const id = c?.randomUUID
    ? c.randomUUID().replace(/-/g, "")
    : Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join("");
  return `${prefix}_${id}`;
};

export type AdminWalletOp =
  | { op: "ensure"; walletId: string; ownerType?: string }
  | { op: "initGlobal" }
  | { op: "setBalance"; walletId: string; balance: number; reason?: string; adjustGlobal?: boolean }
  | { op: "adjust"; walletId: string; amount: number; direction: "credit" | "debit"; reason?: string }
  | { op: "payout"; walletId: string; amount: number; notes?: string }
  | { op: "batchSettle" }
  | { op: "approveWalletRequest" | "rejectWalletRequest"; requestId: string };

export const adminWalletOp = (payload: AdminWalletOp) => call("adminWalletOp", payload);

export const adminPayuVerify = (txnid: string) => call("adminPayuOp", { op: "verify", txnid });
export const adminPayuRefund = (txnid: string, amount?: number, reason?: string, allowNegative = false) =>
  call("adminPayuOp", { op: "refund", txnid, amount, reason, allowNegative });

/** Human message from a Firebase callable error. */
export const callableMessage = (e: any, fallback = "Something went wrong"): string => {
  const m = String(e?.message || "").replace(/^(FirebaseError:\s*)?/, "");
  return m || fallback;
};
